import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { useEstateContext } from '@/lib/EstateContext';
import { useSubscription } from '@/hooks/useSubscription';
import { estatesService } from '@/services';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Check, 
  CreditCard, 
  Shield, 
  Zap, 
  Users, 
  TrendingUp,
  ArrowLeft,
  Loader2,
  Sparkles,
  Tag
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { createPageUrl } from '@/utils';
import { supabase } from '@/lib/supabase';

const pricingTiers = [
  {
    tier: 'small',
    name: 'Small Estate',
    price: 599,
    discountedPrice: 539,
    unitRange: 'Up to 50 homes',
    description: 'For estates with up to 50 homes',
    features: [
      'Up to 50 homes',
      'Full FixMyHood platform included',
      'Internal issue tracking',
      'Contractor management',
      'Monthly reports',
      'Export CSV data',
      'Unlimited reports',
      'Email support',
    ],
  },
  {
    tier: 'medium',
    name: 'Medium Estate',
    price: 1149,
    discountedPrice: 1034,
    unitRange: '51–200 homes',
    description: 'For estates with 51–200 homes',
    features: [
      '51–200 homes',
      'Full FixMyHood platform included',
      'Internal issue tracking',
      'Contractor management',
      'Monthly reports',
      'Export CSV data',
      'Unlimited reports',
      'Email support',
    ],
    popular: true,
  },
  {
    tier: 'large',
    name: 'Large Estate',
    price: 1999,
    discountedPrice: 1799,
    unitRange: '201+ homes',
    description: 'For estates with 201+ homes',
    features: [
      '201+ homes',
      'Full FixMyHood platform included',
      'Internal issue tracking',
      'Contractor management',
      'Monthly reports',
      'Export CSV data',
      'Unlimited reports',
      'Email support',
    ],
  },
];

export default function UpgradePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeEstateId } = useEstateContext();
  const { subscription, isLoading: subscriptionLoading } = useSubscription();
  const estateId = activeEstateId || user?.estate_id;
  const DISCOUNT = 0.10;

  const { data: myEstatesForDiscount = [] } = useQuery({
    queryKey: ['my_estates_for_discount', user?.id],
    queryFn: () => estatesService.getMyEstates(user.id),
    enabled: !!user?.id && user?.user_role === 'estate_manager',
    staleTime: 1000 * 60,
  });
  const estateCount = myEstatesForDiscount.length;
  const hasDiscount = estateCount >= 2;
  const [processingTier, setProcessingTier] = useState(null);

  if (subscriptionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
        <Loader2 className="w-12 h-12 animate-spin text-[#1883FF]" />
      </div>
    );
  }

  const handleCheckout = async (tier) => {
    setProcessingTier(tier);
    
    try {
      if (!subscription) {
        toast.error('Unable to load subscription data');
        return;
      }

      // Call Paystack checkout API
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch('/api/paystack-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          tier: tier,
          estateId: estateId,
          applyDiscount: hasDiscount,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      // Redirect to Paystack checkout page (simpler, more reliable)
      window.location.href = data.authorization_url;
      
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout', {
        description: error.message || 'Please try again or contact support'
      });
      setProcessingTier(null);
    }
  };

  const getCurrentTier = () => {
    if (!subscription) return null;
    return pricingTiers.find(t => t.tier === subscription.subscription_tier);
  };

  const currentTier = getCurrentTier();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate(createPageUrl('EstateDashboard'))}
              className="border-gray-200 hover:bg-gray-50 rounded-xl"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
                Upgrade Your Plan
              </h1>
              <p className="text-slate-600 dark:text-slate-300 mt-2">
                Plans are priced based on the size of your estate. All plans include full access to all features.
              </p>
            </div>
          </div>

          {/* Current Status Banner */}
          {subscription && (
            <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-blue-900 flex items-center gap-2 mb-2">
                    <Sparkles className="w-5 h-5" />
                    Current Status
                  </h3>
                  <p className="text-blue-800">
                    {subscription.isInTrial && (
                      <>
                        <strong>Free Trial</strong> - {subscription.daysRemaining} days remaining
                      </>
                    )}
                    {subscription.isInGrace && (
                      <>
                        <strong>Grace Period</strong> - {subscription.daysRemaining} days to upgrade
                      </>
                    )}
                    {subscription.isExpired && (
                      <>
                        <strong>Expired</strong> - Upgrade to restore full access
                      </>
                    )}
                    {subscription.isActive && (
                      <>
                        <strong>Active</strong> - {currentTier?.name || 'Subscribed'}
                      </>
                    )}
                  </p>
                  {currentTier && (
                    <p className="text-sm text-blue-700 mt-1">
                      Current plan: {currentTier.name} ({currentTier.unitRange})
                    </p>
                  )}
                </div>
                {subscription.hasWriteAccess && (
                  <Badge className="bg-green-100 text-green-700 border-green-300">
                    <Check className="w-3 h-3 mr-1" />
                    Full Access
                  </Badge>
                )}
              </div>
            </Card>
          )}
        </motion.div>

        {/* Multi-estate discount banner */}
        {hasDiscount && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Card className="p-4 bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Tag className="w-4 h-4 text-emerald-700" />
                </div>
                <div>
                  <p className="font-semibold text-emerald-800">10% multi-estate discount applied</p>
                  <p className="text-sm text-emerald-700">You manage {estateCount} estates — prices below already reflect your discount.</p>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Pricing Cards */}}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {pricingTiers.map((tier, index) => {
            const isCurrentTier = currentTier?.tier === tier.tier;
            const canUpgrade = !isCurrentTier && subscription?.hasWriteAccess === false;
            
            return (
              <motion.div
                key={tier.tier}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card 
                  className={`relative p-6 h-full flex flex-col ${
                    tier.popular
                      ? 'border-2 border-[#1883FF] shadow-xl' 
                      : 'border-slate-200'
                  }`}
                >
                  {tier.popular && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#1883FF] text-white">
                      Most Popular
                    </Badge>
                  )}
                  
                  {isCurrentTier && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white">
                      Current Plan
                    </Badge>
                  )}

                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                      {tier.name}
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                      {tier.description}
                    </p>
                    {hasDiscount ? (
                      <div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-4xl font-bold text-slate-900 dark:text-white">
                            R{tier.discountedPrice}
                          </span>
                          <span className="text-slate-600 dark:text-slate-400">/month</span>
                        </div>
                        <p className="text-sm text-slate-400 line-through mt-0.5">R{tier.price}/month</p>
                      </div>
                    ) : (
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold text-slate-900 dark:text-white">
                          R{tier.price}
                        </span>
                        <span className="text-slate-600 dark:text-slate-400">/month</span>
                      </div>
                    )}
                    <p className="text-sm text-slate-500 mt-1">{tier.unitRange}</p>
                  </div>

                  <ul className="space-y-3 mb-6 flex-1">
                    {tier.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-700 dark:text-slate-300">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => handleCheckout(tier.tier)}
                    disabled={isCurrentTier || processingTier !== null}
                    className={`w-full rounded-[24px] md:rounded-[40px] ${
                      isCurrentTier
                        ? 'bg-white text-gray-700 border-2 border-gray-300 hover:bg-gray-50'
                        : tier.popular
                        ? 'bg-[#1883FF] hover:bg-[#0066CC]'
                        : 'bg-slate-900 hover:bg-slate-800'
                    } disabled:opacity-50`}
                  >
                    {processingTier === tier.tier && (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    )}
                    {isCurrentTier ? (
                      'Current Plan'
                    ) : canUpgrade ? (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Upgrade Now
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Subscribe
                      </>
                    )}
                  </Button>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Custom Pricing */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="p-8 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 border-slate-300">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                  Enterprise / Custom
                </h3>
                <p className="text-slate-600 dark:text-slate-400 mb-4">
                  For estates with 300+ units or specialized requirements
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                    <TrendingUp className="w-4 h-4 text-blue-600" />
                    Unlimited units
                  </li>
                  <li className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                    <Users className="w-4 h-4 text-blue-600" />
                    White-label options
                  </li>
                  <li className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                    <Shield className="w-4 h-4 text-blue-600" />
                    On-premise deployment
                  </li>
                  <li className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                    <Zap className="w-4 h-4 text-blue-600" />
                    Custom integrations
                  </li>
                </ul>
              </div>
              <Button
                size="lg"
                variant="outline"
                onClick={() => {
                  window.open('mailto:support@fixmyhood.com?subject=Enterprise%20Inquiry', '_blank');
                }}
                className="border-slate-300 hover:bg-slate-200 dark:border-slate-600"
              >
                Contact Sales
              </Button>
            </div>
          </Card>
        </motion.div>

        {/* FAQ / Benefits */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-12 text-center"
        >
          <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">
            All plans include
          </h3>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-600 dark:text-slate-400">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-green-600" />
              Secure data storage
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-blue-600" />
              Real-time updates
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-orange-600" />
              Email support
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-purple-600" />
              Regular feature updates
            </div>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-500 mt-6">
            Cancel anytime. No hidden fees. Data remains yours forever.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
